// app/shared/components/display/PersonalizationProfileDisplayComponent.tsx
import {
  Building2,
  Settings,
  Target,
  User,
  Zap
} from 'lucide-react';
import NextImage from 'next/image';
import React from 'react';

// Icon mapping for local SVG files
const getLocalIconPath = (toolName: string): string | null => {
  const iconMap: Record<string, string> = {
    'ChatGPT': '/images/home_page_icons/openai_icon.svg',
    'Claude': '/images/home_page_icons/claude_icon.svg',
    'Midjourney': '/images/home_page_icons/midjourney_icon.svg',
    'Eleven Labs': '/images/home_page_icons/elevenlabs_icon.svg',
    'Crew Ai': '/images/home_page_icons/crewai_icon.svg',
    'Kling AI': '/images/home_page_icons/kling_icon.svg',
    'Slack': '/images/home_page_icons/slack_icon.svg',
    'Notion': '/images/home_page_icons/notion_icon.svg',
    'HubSpot': '/images/home_page_icons/hubspot_icon.svg',
    'N8N': '/images/home_page_icons/n8n_icon.svg',
    'Airtable': '/images/home_page_icons/airtable_icon.svg',
    'Mixpanel': '/images/home_page_icons/mixpanel_icon.svg',
  };
  
  return iconMap[toolName] || null;
};

// Mock data for the personalization display
const aiTools = [
  { id: 1, name: 'ChatGPT', logo_url: getLocalIconPath('ChatGPT') },
  { id: 2, name: 'Claude', logo_url: getLocalIconPath('Claude') },
  { id: 3, name: 'Midjourney', logo_url: getLocalIconPath('Midjourney') },
  { id: 4, name: 'Eleven Labs', logo_url: getLocalIconPath('Eleven Labs') },
  { id: 5, name: 'Crew Ai', logo_url: getLocalIconPath('Crew Ai') },
  { id: 6, name: 'Kling AI', logo_url: getLocalIconPath('Kling AI') },
];

const businessTools = [
  { id: 1, name: 'Slack', logo_url: getLocalIconPath('Slack') },
  { id: 2, name: 'Notion', logo_url: getLocalIconPath('Notion') },
  { id: 3, name: 'HubSpot', logo_url: getLocalIconPath('HubSpot') },
  { id: 4, name: 'N8N', logo_url: getLocalIconPath('N8N') },
  { id: 5, name: 'Airtable', logo_url: getLocalIconPath('Airtable') },
  { id: 6, name: 'Mixpanel', logo_url: getLocalIconPath('Mixpanel') },
];

const userProfileData = [
  { iconName: 'User', label: 'Experience', value: 'Beginner' },
  { iconName: 'Target', label: 'Tech Level', value: 'Beginner' },
  { iconName: 'Zap', label: 'AI Experience', value: 'Beginner' },
];

const companyProfileData = [
  { iconName: 'Building2', label: 'Company Stage', value: 'Regional Hospital' },
  { iconName: 'User', label: 'Team Size', value: '500+ employees' },
  { iconName: 'Target', label: 'Industry', value: 'Healthcare' },
];

export const PersonalizationProfileDisplayComponent: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Three Column Layout - Exact replica from homepage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-auto lg:h-[400px]">
        
        {/* Left Column: AI Tools */}
        <div className="rounded-lg p-6 h-full flex flex-col">
          <h4 className="text-lg font-semibold text-gray-800 mb-6">
            Familiar AI Tools
          </h4>
          <div className="grid grid-cols-2 gap-1 flex-1">
            {aiTools.slice(0, 6).map((tool) => (
              <div key={tool.id} className="flex flex-col items-center gap-1 p-2">
                <div className="w-10 h-10 rounded-full bg-transparent flex items-center justify-center overflow-hidden">
                  {tool.logo_url ? (
                    <NextImage 
                      src={tool.logo_url} 
                      alt={`${tool.name} logo`}
                      width={40}
                      height={40}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <Zap className="w-10 h-10 text-yellow" />
                  )}
                </div>
                <span className="text-xs font-medium text-black text-center">{tool.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Middle Column: Profile */}
        <div className="bg-beige-dark rounded-lg p-6 h-full flex flex-col">
          <h4 className="text-lg font-semibold text-gray-800 mb-6">
            Profile
          </h4>
          
          {/* Profile Analysis - 2 Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1">
            {/* User Profile Column */}
            <div>
              <h5 className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">User Profile</h5>
              <div className="space-y-4">
                {userProfileData.map((item, index) => (
                  <div key={index}>
                    <div className="flex items-center gap-2 mb-1">
                      {item.iconName === 'User' && <User className="w-4 h-4 text-gray-800" />}
                      {item.iconName === 'Target' && <Target className="w-4 h-4 text-gray-800" />}
                      {item.iconName === 'Zap' && <Zap className="w-4 h-4 text-gray-800" />}
                      <span className="text-sm font-medium text-gray-600">{item.label}:</span>
                    </div>
                    <p className="text-base text-gray-800 font-medium ml-6">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Company Profile Column */}
            <div>
              <h5 className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">Company Context</h5>
              <div className="space-y-4">
                {companyProfileData.map((item, index) => (
                  <div key={index}>
                    <div className="flex items-center gap-2 mb-1">
                      {item.iconName === 'Building2' && <Building2 className="w-4 h-4 text-gray-800" />}
                      {item.iconName === 'User' && <User className="w-4 h-4 text-gray-800" />}
                      {item.iconName === 'Target' && <Target className="w-4 h-4 text-gray-800" />}
                      <span className="text-sm font-medium text-gray-600">{item.label}:</span>
                    </div>
                    <p className="text-base text-gray-800 font-medium ml-6">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Business Tools */}
        <div className="rounded-lg p-6 h-full flex flex-col">
          <h4 className="text-lg font-semibold text-gray-800 mb-6">
            Business Tools
          </h4>
          <div className="grid grid-cols-2 gap-1 flex-1">
            {businessTools.slice(0, 6).map((tool) => (
              <div key={tool.id} className="flex flex-col items-center gap-1 p-2">
                <div className="w-10 h-10 rounded-full bg-transparent flex items-center justify-center overflow-hidden">
                  {tool.logo_url ? (
                    <NextImage 
                      src={tool.logo_url} 
                      alt={`${tool.name} logo`}
                      width={40}
                      height={40}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <Settings className="w-10 h-10 text-orange" />
                  )}
                </div>
                <span className="text-xs font-medium text-black text-center">{tool.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
